/* ============================================================
   SAHAB SYSTEMS — Public per-employee card (route /:slug)
   Layout C: Core Motif — giant brand mark behind centered avatar.
   Ported from design/card.jsx (English-only; bilingual footer).
   ============================================================ */
import html2canvas from "html2canvas";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { BilingualLockup, I, Lockup, Mark, initials } from "../lib/brand";
import { SahabQR } from "../lib/SahabQR";
import {
  type EmployeePublic,
  HttpError,
  getEmployeeBySlug,
  shortCodeUrl,
  vcardUrl,
} from "../lib/api";
import { isLoggedIn } from "../lib/auth";
import { whatsappLink } from "../lib/employee";
import { safeBackgroundImage } from "../lib/photoUrl";

const STR = {
  company: "Sahab Systems",
  chat: "Chat on WhatsApp",
  save: "Save to Contacts",
  savePhoto: "Save image",
  qrShare: "Scan to share this card",
  scanHint: "Point a camera here",
  mobile: "Mobile",
  email: "Email",
  address: "Address",
  linkedin: "LinkedIn",
  website: "Website",
  companyProfile: "Company Profile",
  companyProfileHint: "PDF",
  companyLinkedin: "Sahab Systems on LinkedIn",
  companyProfileOpening: "Opening company profile…",
  verified: "Verified contact",
  footerNote: "Digital business card · Generated by Sahab Systems",
  loading: "Loading…",
  notFoundTitle: "Card not found",
  notFoundBody: "We couldn't find that employee. Double-check the URL or scan the QR again.",
  errorTitle: "Couldn't load the card",
};

// Pre-onboarding employees have no full_name yet (set by the employee
// during onboarding, not the admin) — fall back to a placeholder so the
// public card never renders a blank name if it's visited before that.
type DisplayEmployee = EmployeePublic & { full_name: string };

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; status: number | null }
  | { kind: "ok"; employee: DisplayEmployee };

function useEmployee(slug: string | undefined): LoadState {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  useEffect(() => {
    if (!slug) {
      setState({ kind: "error", status: 404 });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    getEmployeeBySlug(slug)
      .then((emp) => {
        if (!cancelled) {
          setState({
            kind: "ok",
            employee: { ...emp, full_name: emp.full_name || "New team member" },
          });
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = e instanceof HttpError ? e.status : null;
        setState({ kind: "error", status });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);
  return state;
}

/* ============================================================
   Avatar — real photo when employee.photo_url is set, else the
   branded placeholder from the design (initials + PHOTO watermark
   + faint diagonal lines + brand mark watermark in the corner).
   ============================================================ */
/* Avatar — `background-image` + `background-size` + `background-position`
   instead of `<img>` + `object-fit`. This gives both X and Y axes real
   slack at zoom > 100% regardless of source aspect ratio:
   - At zoom = 100: behaves exactly like `object-fit: cover` (smaller
     source dimension fills the container).
   - At zoom > 100: detects source orientation, then uses `${zoom}% auto`
     (portrait / square) or `auto ${zoom}%` (landscape) so the binding
     axis grows past the container, opening slack on both axes.
   The off-screen `<img>` only exists to report intrinsic dimensions. */
function useImageOrientation(src: string): "portrait" | "landscape" | "square" | "loading" {
  const [o, setO] = useState<"portrait" | "landscape" | "square" | "loading">("loading");
  useEffect(() => {
    if (!src) {
      setO("loading");
      return;
    }
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth > img.naturalHeight) setO("landscape");
      else if (img.naturalWidth < img.naturalHeight) setO("portrait");
      else setO("square");
    };
    img.onerror = () => setO("portrait"); // safest default for headshots
    img.src = src;
  }, [src]);
  return o;
}

function ZoomCropAvatar({
  src,
  size,
  focalX,
  focalY,
  zoom,
  ring,
}: {
  src: string;
  alt: string;
  size: number;
  focalX: number;
  focalY: number;
  zoom: number;
  ring: boolean;
}) {
  const orientation = useImageOrientation(src);
  const shadow = ring
    ? "0 0 0 5px var(--field), 0 0 0 6px var(--line-2)"
    : "none";
  const bgSize =
    zoom <= 100
      ? "cover"
      : orientation === "landscape"
        ? `auto ${zoom}%`
        : `${zoom}% auto`;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        border: ring ? "1px solid var(--line)" : "none",
        boxShadow: shadow,
        flex: "none",
        background: "var(--surface-2)",
        backgroundImage: safeBackgroundImage(src),
        backgroundSize: bgSize,
        backgroundPosition: `${focalX}% ${focalY}%`,
        backgroundRepeat: "no-repeat",
      }}
    />
  );
}

function Avatar({
  employee,
  size = 138,
  ring = true,
}: {
  employee: DisplayEmployee;
  size?: number;
  ring?: boolean;
}) {
  const shadow = ring
    ? "0 0 0 5px var(--field), 0 0 0 6px var(--line-2)"
    : "none";

  if (employee.photo_url) {
    return (
      <ZoomCropAvatar
        src={employee.photo_url}
        alt={employee.full_name}
        size={size}
        focalX={employee.photo_focal_x}
        focalY={employee.photo_focal_y}
        zoom={employee.photo_zoom}
        ring={ring}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        position: "relative",
        flex: "none",
        background: "var(--surface-2)",
        border: ring ? "1px solid var(--line)" : "none",
        boxShadow: shadow,
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.5,
          background:
            "repeating-linear-gradient(135deg, rgba(126,201,224,0.13) 0 1px, transparent 1px 9px)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(120% 120% at 50% 0%, rgba(126,201,224,0.15), transparent 60%)",
        }}
      />
      <div
        style={{
          position: "absolute",
          right: size * 0.07,
          bottom: size * 0.06,
          opacity: 0.16,
          color: "var(--text)",
        }}
      >
        <Mark size={size * 0.3} />
      </div>
      <span
        style={{
          fontWeight: 700,
          fontSize: size * 0.34,
          letterSpacing: "-.02em",
          color: "var(--text)",
          zIndex: 1,
        }}
      >
        {initials(employee.full_name)}
      </span>
      <span
        className="mono"
        style={{
          position: "absolute",
          top: size * 0.09,
          left: "50%",
          transform: "translateX(-50%)",
          fontSize: 8.5,
          letterSpacing: ".2em",
          color: "var(--text-3)",
          zIndex: 1,
        }}
      >
        PHOTO
      </span>
    </div>
  );
}

/* ============================================================
   WhatsApp pulse CTA — primary action button with the cyan ring
   animation defined in the design tokens (sahab-ring keyframes).
   Falls back to a disabled state when the employee has no number.
   ============================================================ */
function PulseCTA({ wa, pulse = true }: { wa: string | null; pulse?: boolean }) {
  if (!wa) {
    return (
      <button
        type="button"
        className="btn"
        disabled
        style={{
          width: "100%",
          padding: "16px 18px",
          fontSize: 16,
          borderRadius: "var(--r-md)",
          background: "var(--line-2)",
          color: "var(--text-3)",
          cursor: "not-allowed",
          border: "1px solid var(--line)",
        }}
        title="WhatsApp not configured yet"
      >
        {I.whatsapp({ size: 20 })}
        <span>{STR.chat}</span>
      </button>
    );
  }
  return (
    <a
      className="btn btn-primary"
      href={wa}
      target="_blank"
      rel="noreferrer"
      style={{
        position: "relative",
        width: "100%",
        padding: "16px 18px",
        fontSize: 16,
        borderRadius: "var(--r-md)",
      }}
    >
      {pulse && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "var(--r-md)",
            border: "1.5px solid var(--signal)",
            animation: "sahab-ring 2.6s var(--ease) infinite",
            pointerEvents: "none",
          }}
        />
      )}
      {I.whatsapp({ size: 20 })}
      <span>{STR.chat}</span>
    </a>
  );
}

/* Save the rendered PNG as a plain download — same behavior as the
   admin's QR PNG export. iOS handles this by routing to "Downloads"
   (not Photos), but the share-sheet approach hijacked desktop without
   actually helping iOS, so the simpler path wins. */
function saveOrShareImage(blob: Blob, filename: string, _title: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ============================================================
   Save row — .vcf download + Telemetry-layout PNG capture.
   The PNG renders the same employee data laid out as Layout B
   (Telemetry Sheet) so the saved image carries readable name +
   title + mobile / email / address / linkedin / website rows —
   useful when forwarding via WhatsApp where a URL chip won't do.
   ============================================================ */
function SaveRow({
  employee,
  telemetryRef,
  onFlash,
}: {
  employee: DisplayEmployee;
  telemetryRef: React.RefObject<HTMLDivElement>;
  onFlash: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function saveImage() {
    const node = telemetryRef.current;
    if (!node) return;
    setBusy(true);
    try {
      // Wait for webfonts (Manrope / IBM Plex Mono / Noto Kufi Arabic)
      // to fully load — otherwise the snapshot renders system fallbacks.
      if (document.fonts?.ready) await document.fonts.ready;
      // One paint tick so the offscreen TelemetrySheet has measured its
      // children before the snapshot.
      await new Promise((r) => setTimeout(r, 80));
      const canvas = await html2canvas(node, {
        scale: 2.5,
        backgroundColor: "#F2EFE8",
        useCORS: true, // honor Access-Control-Allow-Origin on the photo proxy
        allowTaint: false,
        logging: false,
        windowWidth: 440,
        width: 440,
        height: node.offsetHeight,
      });
      const blob: Blob = await new Promise((resolve, reject) =>
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
          "image/png",
        ),
      );
      saveOrShareImage(blob, `${employee.slug}-card.png`, employee.full_name);
      onFlash(`${STR.savePhoto} ✓`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      onFlash(`Save failed: ${msg.slice(0, 80)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", gap: 10 }}>
      <a
        href={vcardUrl(employee.slug)}
        download={`${employee.slug}.vcf`}
        className="btn btn-ghost"
        style={{ flex: 1 }}
        onClick={() => onFlash(`${STR.save} ✓`)}
      >
        {I.download({ size: 18 })}
        <span>{STR.save}</span>
      </a>
      <button
        type="button"
        className="btn btn-ghost"
        style={{ flex: "none", width: 52 }}
        aria-label={STR.savePhoto}
        title={STR.savePhoto}
        disabled={busy}
        onClick={() => void saveImage()}
      >
        {I.share({ size: 18 })}
      </button>
    </div>
  );
}

/* ============================================================
   Company profile — one company-wide PDF hosted on R2 and served with
   Content-Disposition: inline, so it opens in the phone's built-in PDF
   viewer (save/share is one tap away there) rather than dumping a 12MB
   file into Downloads. New tab, so the card stays behind it.

   Kept out of the circular quick-link row on purpose: a 6th 48px circle
   doesn't fit on a 360px phone, and a marketing asset deserves a
   readable label anyway.
   ============================================================ */
function ProfileRow({ href, onFlash }: { href: string; onFlash: (m: string) => void }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="btn btn-ghost"
      style={{ width: "100%" }}
      onClick={() => onFlash(STR.companyProfileOpening)}
    >
      {I.note({ size: 18 })}
      <span>{STR.companyProfile}</span>
      <span
        className="mono"
        style={{
          fontSize: 9.5,
          letterSpacing: ".14em",
          color: "var(--text-3)",
        }}
      >
        {STR.companyProfileHint}
      </span>
    </a>
  );
}

/* ============================================================
   Quick-link row — 5 circular icon buttons (mobile / email /
   address / linkedin / website). Only populated fields render.
   ============================================================ */
type LinkSpec = {
  k: string;
  icon: (p?: { size?: number; style?: CSSProperties }) => JSX.Element;
  label: string;
  href: string;
};

function quickLinksFor(emp: EmployeePublic): LinkSpec[] {
  const links: LinkSpec[] = [];
  if (emp.phone)
    links.push({
      k: "mobile",
      icon: I.phone,
      label: STR.mobile,
      href: `tel:${emp.phone.replace(/\s/g, "")}`,
    });
  if (emp.email)
    links.push({ k: "email", icon: I.mail, label: STR.email, href: `mailto:${emp.email}` });
  if (emp.address)
    links.push({
      k: "address",
      icon: I.pin,
      label: STR.address,
      href: `https://maps.google.com/?q=${encodeURIComponent(emp.address)}`,
    });
  if (emp.linkedin_url)
    links.push({
      k: "linkedin",
      icon: I.linkedin,
      label: STR.linkedin,
      href: emp.linkedin_url,
    });
  if (emp.website_url)
    links.push({
      k: "website",
      icon: I.globe,
      label: STR.website,
      href: emp.website_url,
    });
  return links;
}

function IconLink({ icon, label, href }: { icon: LinkSpec["icon"]; label: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 7,
        textDecoration: "none",
        color: "var(--text)",
        flex: 1,
        minWidth: 0,
      }}
    >
      <span
        className="iconlink-btn"
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          border: "1px solid var(--line)",
          background: "var(--surface)",
          transition: "all .2s var(--ease)",
        }}
      >
        {icon({ size: 19 })}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 9.5,
          letterSpacing: ".12em",
          textTransform: "uppercase",
          color: "var(--text-3)",
        }}
      >
        {label}
      </span>
    </a>
  );
}

/* ============================================================
   QR block at the bottom of the card — branded scannable QR.
   ============================================================ */
function QRBlock({ employee }: { employee: DisplayEmployee }) {
  const qrTarget = shortCodeUrl(employee.short_code);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        border: "1px solid var(--line)",
        borderRadius: "var(--r-lg)",
        padding: 16,
        background: "var(--surface)",
      }}
    >
      <div
        style={{
          background: "var(--field)",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-md)",
          padding: 8,
          flex: "none",
        }}
      >
        <SahabQR url={qrTarget} size={96} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div className="tlm">{STR.qrShare}</div>
        <div style={{ fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.35 }}>
          {STR.scanHint}
        </div>
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            letterSpacing: ".18em",
            color: "var(--text-3)",
            marginTop: 2,
          }}
        >
          {employee.short_code}
        </div>
      </div>
    </div>
  );
}

/* The footer is the card's company zone — the lockup, the generated-by
   note, and Sahab's own LinkedIn page. Kept distinct from the quick-link
   row above it, which carries the individual's personal profile. */
function CardFooter({ companyLinkedinUrl }: { companyLinkedinUrl?: string | null }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 12,
        paddingTop: 18,
        marginTop: 4,
        borderTop: "1px solid var(--line-2)",
      }}
    >
      <BilingualLockup height={24} />
      {companyLinkedinUrl && (
        <a
          href={companyLinkedinUrl}
          target="_blank"
          rel="noreferrer"
          className="mono"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            fontSize: 9.5,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "var(--text-3)",
            textDecoration: "none",
          }}
        >
          {I.linkedin({ size: 13 })}
          <span>{STR.companyLinkedin}</span>
        </a>
      )}
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          letterSpacing: ".14em",
          color: "var(--text-3)",
          textAlign: "center",
        }}
      >
        {STR.footerNote.toUpperCase()}
      </div>
    </div>
  );
}

/* ============================================================
   Layout B — Telemetry Sheet. Not the visible layout (Layout C
   wins for that), but we render it offscreen so the "Save image"
   button can capture it to a PNG with readable contact rows.
   ============================================================ */
function TelemetrySheet({ employee }: { employee: DisplayEmployee }) {
  const pad = 26;
  const qrTarget = shortCodeUrl(employee.short_code);
  const links = quickLinksFor(employee);
  return (
    <div
      style={{
        width: 440,
        background: "var(--field)",
        borderRadius: "var(--r-xl)",
        border: "1px solid var(--line)",
        boxShadow: "var(--elev)",
        overflow: "hidden",
        color: "var(--text)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `16px ${pad}px` }}>
        <Lockup height={20} />
        <span
          className="mono"
          style={{
            fontSize: 10,
            letterSpacing: ".16em",
            color: "var(--text-3)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span className="dot" style={{ background: "var(--signal)" }} />
          LIVE · CARD&nbsp;v1
        </span>
      </div>
      <hr className="hair" />
      <div style={{ padding: pad, display: "flex", gap: 18, alignItems: "center" }}>
        <SquareAvatar employee={employee} size={96} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
          {/* Aggressive line-height + padding-bottom because html2canvas
              chops descender pixels (g/j/p/q/y) below the glyph baseline
              when leading is anywhere near the default. */}
          <h1
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: "-.02em",
              lineHeight: 1.8,
              paddingBottom: 8,
            }}
          >
            {employee.full_name}
          </h1>
          {employee.title && (
            <div
              style={{
                fontSize: 14,
                color: "var(--text-2)",
                fontWeight: 500,
                lineHeight: 1.7,
                paddingBottom: 6,
              }}
            >
              {employee.title}
            </div>
          )}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              color: "var(--text-2)",
              fontSize: 13.5,
              lineHeight: 1.7,
              paddingBottom: 6,
            }}
          >
            <Mark size={14} />
            <span>{employee.company || STR.company}</span>
          </div>
        </div>
      </div>
      {/* Hairline separator between the header card and the contact rows
          — the previous CODE / LOCATION / STATUS instrument rail was
          internal scaffolding that didn't add value to the visitor, so it's
          removed and the freed space goes to the contact rows below. */}
      <hr className="hair" style={{ margin: `0 ${pad}px` }} />
      <div style={{ padding: `0 ${pad}px ${pad}px` }}>
        {links.map((l, i) => (
          <div
            key={l.k}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              padding: "18px 4px",
              borderTop: i === 0 ? "none" : "1px solid var(--line-2)",
            }}
          >
            <span
              style={{ color: "var(--text-2)", display: "flex", alignItems: "center" }}
            >
              {l.icon({ size: 18 })}
            </span>
            <span
              className="mono"
              style={{
                fontSize: 10,
                letterSpacing: ".18em",
                color: "var(--text-3)",
                width: 80,
                textTransform: "uppercase",
                lineHeight: 1.6,
              }}
            >
              {l.label}
            </span>
            {/* Smaller font (10.5) so long LinkedIn URLs with the
                user-hash suffix fit without ellipsis. lineHeight 1.8 +
                paddingBottom 8 preserves descender pixels through the
                html2canvas raster. */}
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 10.5,
                color: "var(--text-2)",
                whiteSpace: "nowrap",
                direction: "ltr",
                textAlign: "left",
                lineHeight: 1.8,
                paddingBottom: 8,
                overflowX: "hidden",
                overflowY: "visible",
                textOverflow: "ellipsis",
              }}
            >
              {displayValueFor(l, employee)}
            </span>
          </div>
        ))}
      </div>
      <div style={{ padding: `0 ${pad}px ${pad}px` }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            border: "1px solid var(--line)",
            borderRadius: "var(--r-lg)",
            padding: 16,
            background: "var(--surface)",
          }}
        >
          <div
            style={{
              background: "var(--field)",
              border: "1px solid var(--line)",
              borderRadius: "var(--r-md)",
              padding: 8,
              flex: "none",
            }}
          >
            <SahabQR url={qrTarget} size={84} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div className="tlm">{STR.qrShare}</div>
            <div style={{ fontSize: 13.5, color: "var(--text-2)", lineHeight: 1.35 }}>{STR.scanHint}</div>
            <div
              className="mono"
              style={{ fontSize: 10.5, letterSpacing: ".18em", color: "var(--text-3)", marginTop: 2 }}
            >
              {employee.short_code}
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          padding: `0 ${pad}px ${pad}px`,
          borderTop: "1px solid var(--line-2)",
          paddingTop: 18,
        }}
      >
        <BilingualLockup height={24} />
        <div
          className="mono"
          style={{
            fontSize: 9.5,
            letterSpacing: ".14em",
            color: "var(--text-3)",
            textAlign: "center",
          }}
        >
          {STR.footerNote.toUpperCase()}
        </div>
      </div>
    </div>
  );
}

/* Same-origin photo URL used INSIDE the TelemetrySheet so html-to-image
   can include it in the canvas snapshot. R2's custom-domain endpoint
   doesn't honor CORS, so we proxy through /api/photo-proxy which lives
   on the same origin as the card and explicitly emits
   Access-Control-Allow-Origin. */
function captureSafePhotoUrl(originalUrl: string | null): string | null {
  if (!originalUrl) return null;
  return `/api/photo-proxy?u=${encodeURIComponent(originalUrl)}`;
}

/* Square variant of the avatar for the Telemetry layout's "instrument
   panel" look. Photo respects focal-point + zoom. */
function SquareAvatar({ employee, size }: { employee: DisplayEmployee; size: number }) {
  const safeUrl = captureSafePhotoUrl(employee.photo_url);
  if (safeUrl) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: "var(--r-lg)",
          overflow: "hidden",
          flex: "none",
          background: "var(--surface-2)",
          backgroundImage: `url("${safeUrl}")`,
          backgroundSize: "cover",
          backgroundPosition: `${employee.photo_focal_x}% ${employee.photo_focal_y}%`,
          backgroundRepeat: "no-repeat",
          border: "1px solid var(--line)",
        }}
      />
    );
  }
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "var(--r-lg)",
        background: "var(--surface-2)",
        border: "1px solid var(--line)",
        display: "grid",
        placeItems: "center",
        flex: "none",
        position: "relative",
      }}
    >
      <span style={{ fontWeight: 700, fontSize: size * 0.34, color: "var(--text)" }}>
        {initials(employee.full_name)}
      </span>
      <div
        style={{
          position: "absolute",
          right: size * 0.07,
          bottom: size * 0.06,
          opacity: 0.16,
          color: "var(--text)",
        }}
      >
        <Mark size={size * 0.3} />
      </div>
    </div>
  );
}

/* Convert each link to a human-readable value for the telemetry sheet.
   Aim for the shortest representation that still uniquely identifies
   the visitor — LinkedIn URLs in particular append a slug-hash suffix
   that doesn't fit the row width and isn't useful to a human reader. */
function displayValueFor(l: LinkSpec, emp: EmployeePublic): string {
  switch (l.k) {
    case "mobile":
      return emp.phone ?? "";
    case "email":
      return emp.email ?? "";
    case "address":
      return emp.address ?? "";
    case "linkedin":
      // Keep the URL intact (incl. trailing /<hash>) — just strip the
      // protocol prefix so the value displays as
      // "linkedin.com/in/jafar-albinmousa-19b41b1a/" instead of the
      // full https://www.linkedin.com/...
      return (emp.linkedin_url ?? "").replace(/^https?:\/\/(www\.)?/, "");
    case "website":
      return (emp.website_url ?? "").replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "");
    default:
      return "";
  }
}

/* ============================================================
   Layout C — Core Motif. Centered Mark behind the avatar with a
   radial cyan glow + a thin signal ring around the portrait.
   ============================================================ */
function LayoutC({
  employee,
  wa,
  pulse,
  onFlash,
  telemetryRef,
}: {
  employee: DisplayEmployee;
  wa: string | null;
  pulse: boolean;
  onFlash: (m: string) => void;
  telemetryRef: React.RefObject<HTMLDivElement>;
}) {
  const pad = 26;
  const links = quickLinksFor(employee);
  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <div
        style={{
          position: "relative",
          padding: pad,
          paddingTop: 24,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Lockup height={22} pulse={pulse} />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 16,
            marginTop: 8,
          }}
        >
          <div style={{ position: "relative", display: "grid", placeItems: "center" }}>
            {/* giant faint mark + glow, centered behind the avatar */}
            <div
              className={pulse ? "is-pulsing" : ""}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                color: "var(--text)",
                opacity: 0.06,
                pointerEvents: "none",
              }}
            >
              <Mark size={440} />
            </div>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
                width: 340,
                height: 340,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, var(--signal-tint), transparent 65%)",
                pointerEvents: "none",
              }}
            />
            <div style={{ position: "relative" }}>
              <div
                style={{
                  position: "absolute",
                  inset: -10,
                  borderRadius: "50%",
                  border: "1.5px solid var(--signal)",
                  opacity: 0.5,
                }}
              />
              <Avatar employee={employee} size={150} ring={false} />
            </div>
          </div>
          <div style={{ textAlign: "center", maxWidth: 320 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 25,
                fontWeight: 800,
                letterSpacing: "-.025em",
                lineHeight: 1.22,
              }}
            >
              {employee.full_name}
            </h1>
            {employee.title && (
              <div
                style={{
                  fontSize: 15,
                  color: "var(--text-2)",
                  fontWeight: 500,
                  marginTop: 10,
                }}
              >
                {employee.title}
              </div>
            )}
            <div
              className="mono"
              style={{
                fontSize: 10.5,
                letterSpacing: ".22em",
                color: "var(--text-3)",
                marginTop: 10,
              }}
            >
              {(employee.company || STR.company).toUpperCase()} · {employee.short_code}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          <PulseCTA wa={wa} pulse={pulse} />
        </div>
        <SaveRow employee={employee} telemetryRef={telemetryRef} onFlash={onFlash} />
        {employee.company_profile_url && (
          <ProfileRow href={employee.company_profile_url} onFlash={onFlash} />
        )}
        {links.length > 0 && (
          <div style={{ display: "flex", gap: 6, padding: "4px 0" }}>
            {links.map((l) => (
              <IconLink key={l.k} icon={l.icon} label={l.label} href={l.href} />
            ))}
          </div>
        )}
        <QRBlock employee={employee} />
        <CardFooter companyLinkedinUrl={employee.company_linkedin_url} />
      </div>
    </div>
  );
}

/* ============================================================ */
function CenteredMessage({ title, body }: { title: string; body?: string }) {
  return (
    <main
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
        background: "var(--backdrop)",
        color: "var(--text)",
      }}
    >
      <div style={{ maxWidth: 360, textAlign: "center", display: "flex", flexDirection: "column", gap: 12 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{title}</h1>
        {body && <p style={{ color: "var(--text-2)", fontSize: 14, margin: 0 }}>{body}</p>}
      </div>
    </main>
  );
}

function useFlash(): [string | null, (m: string) => void] {
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!msg) return;
    const t = window.setTimeout(() => setMsg(null), 1900);
    return () => window.clearTimeout(t);
  }, [msg]);
  return [msg, setMsg];
}

export default function CardPage() {
  const { slug } = useParams<{ slug: string }>();
  const state = useEmployee(slug);
  const [flash, fire] = useFlash();

  if (state.kind === "loading") {
    return <CenteredMessage title={STR.loading} />;
  }
  if (state.kind === "error") {
    if (state.status === 404) {
      return <CenteredMessage title={STR.notFoundTitle} body={STR.notFoundBody} />;
    }
    return <CenteredMessage title={STR.errorTitle} body={`HTTP ${state.status ?? "—"}`} />;
  }

  const employee = state.employee;
  const wa = whatsappLink(employee);

  return (
    <CardPageContent employee={employee} wa={wa} flash={flash} fire={fire} />
  );
}

function CardPageContent({
  employee,
  wa,
  flash,
  fire,
}: {
  employee: DisplayEmployee;
  wa: string | null;
  flash: string | null;
  fire: (m: string) => void;
}) {
  const telemetryRef = useRef<HTMLDivElement>(null);
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--backdrop)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Top inset for the installed app, where this page now renders
        // inside the standalone window (PWA scope is "/") and would
        // otherwise start behind the status bar. env() is 0 in a browser.
        padding: "calc(22px + env(safe-area-inset-top)) 14px 40px",
      }}
    >
      {/* public-link chip */}
      <div
        className="mono"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 11,
          color: "var(--text-3)",
          background: "var(--surface)",
          border: "1px solid var(--line)",
          borderRadius: 999,
          padding: "6px 13px",
          marginBottom: 16,
          direction: "ltr",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--signal)" }} />
        vcard.sahabsystems.com/<span style={{ color: "var(--text-2)" }}>{employee.slug}</span>
      </div>
      <div style={{ width: "100%", maxWidth: "var(--maxw)", position: "relative" }}>
        <div
          style={{
            background: "var(--field)",
            borderRadius: "var(--r-xl)",
            border: "1px solid var(--line)",
            boxShadow: "var(--elev)",
            overflow: "hidden",
          }}
        >
          <LayoutC employee={employee} wa={wa} pulse onFlash={fire} telemetryRef={telemetryRef} />
        </div>
        {flash && (
          <div
            className="mono"
            style={{
              position: "fixed",
              left: "50%",
              bottom: 28,
              transform: "translateX(-50%)",
              background: "var(--text)",
              color: "var(--field)",
              padding: "11px 18px",
              borderRadius: 999,
              fontSize: 12.5,
              letterSpacing: ".04em",
              boxShadow: "var(--elev)",
              zIndex: 50,
            }}
          >
            {flash}
          </div>
        )}
      </div>
      {isLoggedIn() && <AppOwnerActions />}
      {/* Offscreen Telemetry-layout card — captured to PNG when
          the user taps the Save image button. position: absolute
          with explicit width (so html-to-image's bounding-box
          calc doesn't return 0) and top: -99999 to keep it out
          of the visible viewport while still in normal layout. */}
      <div
        ref={telemetryRef}
        style={{
          position: "absolute",
          top: -99999,
          left: 0,
          width: 440,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      >
        <TelemetrySheet employee={employee} />
      </div>
    </div>
  );
}

/* Signed-in-only action row, placed below the card the same way the
   public-link chip sits above it — replaces the earlier fixed/floating
   buttons (App.tsx's old AppProfileBadge), which drifted around the
   viewport and at one point covered that chip. Shown whenever a signed-in
   app user views any card (their own or a colleague's) — /app always
   redirects here, so this is the one place the whole app funnels
   through and the natural spot for these links. */
const ownerActionPillStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--text-2)",
  background: "var(--surface)",
  border: "1px solid var(--line)",
  borderRadius: 999,
  padding: "10px 18px",
  textDecoration: "none",
};

function AppOwnerActions() {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        justifyContent: "center",
        marginTop: 18,
        width: "100%",
        maxWidth: "var(--maxw)",
      }}
    >
      <Link to="/app/leads" style={ownerActionPillStyle}>
        My leads
      </Link>
      <Link to="/app/profile" style={ownerActionPillStyle}>
        Edit my card
      </Link>
      <Link to="/app/install" style={ownerActionPillStyle}>
        Install app
      </Link>
      <Link
        to="/app/scan"
        style={{
          ...ownerActionPillStyle,
          background: "var(--signal)",
          borderColor: "transparent",
          color: "var(--ink)",
        }}
      >
        Scan a card
      </Link>
    </div>
  );
}
